import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { OptimizelyExperiments } from 'constants/optimizely'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MobileMultipleRoomsChangeRoomModal from './Modals/MobileMultipleRoomsChangeRoomModal'
import MobileMultipleRoomsRoomOptionPriceDetails from './MobileMultipleRoomsRoomOptionPriceDetails'
import MultipleRoomsInclusions from '../MultipleRoomsInclusions'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { pluralizeToString } from 'lib/string/pluralize'
import { getAvailablePackagesByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import config from 'constants/config'
import { unique } from 'lib/array/arrayUtils'
import Clickable from 'components/Common/Clickable'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import FlashHotelRoomDetailsModal from '../../FlashHotel/FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import GeoContext from 'contexts/geoContext'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'

const RoomContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  offer: App.HotelOffer
  checkIn: string
  room: OfferPageRoom
  roomIndex: number
  availableRoomCount?: number
  isFlightsSelected: boolean
  availableForPurchase: boolean
  packageView: App.OfferPackageOptionView
  availablePackagesByRoom: PackagesOptionsViewsByRoom
  rooms: Array<OfferPageRoom>
}

function MobileMultipleRoomsIndividualRoomOption(props: Props) {
  const { offer, checkIn, room, roomIndex, availableRoomCount, availableForPurchase, isFlightsSelected, packageView, availablePackagesByRoom, rooms } = props

  const { totalFlightPrice } = useOfferPageFlightPrice()

  const roomPax = sumUpOccupancies([room])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  const showModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()

  const firstRoomImageId = packageView.package.roomType?.images?.[0]?.id
  const availablePackages = availablePackagesByRoom[room.id]
  const availablePackagesCount = availablePackages.length

  const availablePackageRoomTypeCount = useMemo(() => {
    return unique(
      availablePackages.map(pkg => pkg.package.id),
    ).length
  }, [availablePackages])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, flightPrice, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected, currentRegionCode, flightPrice])

  const onOpenRoomsModal = useCallback(async() => {
    const packageOption = await showModal<App.HotelPackage | undefined>(
      <MobileMultipleRoomsChangeRoomModal room={room} roomIndex={roomIndex} selectedPackageView={packageView} />,
    )

    if (packageOption) {
      dispatch({
        type: OfferPageActions.selectRoomPackage,
        packageOption,
        room,
      })
    }
  }, [dispatch, room, roomIndex, showModal, packageView])

  const onThumbnailClick = useCallback(() => {
    const images = packageView.package.roomType?.images ?? []
    if (!images.length) return
    showModal(<ImageGalleryLightbox images={images} defaultIndex={0} />)
  }, [showModal, packageView])

  const onTitleClick = useCallback(() => {
    const roomType = packageView.package.roomType
    if (!roomType) return
    showModal(<FlashHotelRoomDetailsModal room={roomType} />)
  }, [showModal, packageView])

  return (
    <RoomContainer>
      <Group direction="vertical" gap={16}>
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            <Group direction="vertical" gap={12}>
              <StructureLabel variant="dark">Room {roomIndex + 1}</StructureLabel>
              <div>
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  <Clickable onClick={onTitleClick}>
                    <Heading variant="heading4">{packageView.package.roomType?.name}</Heading>
                  </Clickable>
                  <Clickable onClick={onTitleClick} aria-label="Room details">
                    <LineInfoCircleIcon />
                  </Clickable>
                </Group>
                <BodyText variant="medium" colour="neutral-three">{formatOccupantsShort(room)}</BodyText>
              </div>
            </Group>
            <Clickable onClick={onThumbnailClick}>
              <Image id={firstRoomImageId} width={80} height={80} dpr={3} />
            </Clickable>
          </Group>
          <LabelGroup>
            {checkIn && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} /> }
            <BookingsCountLabel bookings={packageView.package.roomType?.stats.booked ?? 0} threshold="room" />
          </LabelGroup>
          <LabelGroup>
            {!!packageView.package.roomType?.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>
              {packageView.package.roomType.sizeSqm} m&sup2;
            </StructureLabel>}
            {!!packageView.package.roomType?.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>
              {packageView.package.roomType.numberOfBedrooms} bedrooms
            </StructureLabel>}
          </LabelGroup>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
          />}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
          />
          <MultipleRoomsInclusions offer={offer} packageOption={packageView} />
        </Group>
        <MobileMultipleRoomsRoomOptionPriceDetails room={room} packageOption={packageView}>
          {config.PACKAGE_CHANGE_UPGRADE_ENABLED && (
            <Group direction="vertical" gap={8}>
              <TextButton
                variant="dark"
                kind="secondary"
                fit="flex"
                size="medium"
                startIcon={<LineEditAltIcon />}
                onClick={onOpenRoomsModal}
                disabled={availablePackagesCount <= 1}
              >
                Change room
              </TextButton>
              <Group direction="horizontal" horizontalAlign="center">
                <PriceRowCaption>
                  {availablePackagesCount <= 1 && 'No additional options'}
                  {availablePackageRoomTypeCount > 1 && <>{pluralizeToString('room option', availablePackageRoomTypeCount)} available</>}
                </PriceRowCaption>
              </Group>
            </Group>
          )}
        </MobileMultipleRoomsRoomOptionPriceDetails>
      </Group>
    </RoomContainer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer!,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  rooms: state.rooms,
}), MobileMultipleRoomsIndividualRoomOption)
